import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ContactPopup } from '../components/ContactPopup';
import { Toaster } from '../components/ui/sonner';
import { businessInfo } from '../mock';

export const PrivacyPolicy = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const handleContactClick = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy | TradeMark Registry - Your Data Protection Rights</title>
        <meta name="description" content="Read our comprehensive privacy policy to understand how TradeMark Registry collects, uses, and protects your personal information. CCPA compliant with strong data security measures." />
        <meta name="keywords" content="privacy policy, data protection, CCPA, user privacy, personal information, data security, trademark services privacy" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://trademark-pro-8.preview.emergentagent.com/privacy-policy" />
      </Helmet>
      
      <Header onContactClick={handleContactClick} />
      
      <main className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4">
            Privacy Policy
          </h1>
          <p className="text-[#8D6E63] mb-8">
            Last Updated: December 2025
          </p>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">1. Introduction</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Welcome to {businessInfo.name}. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our trademark registration services.
              </p>
              <p className="text-[#8D6E63] leading-relaxed">
                By using our services, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">2.1 Personal Information</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Request a consultation or contact us through our website</li>
                <li>Register for our trademark services</li>
                <li>Subscribe to our newsletter or marketing communications</li>
                <li>Participate in surveys or provide feedback</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                The personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Name and contact information (email address, phone number, mailing address)</li>
                <li>Business information (company name, business type, industry)</li>
                <li>Trademark information (mark details, specimens, descriptions)</li>
                <li>Payment and billing information</li>
                <li>Government-issued identification (as required for trademark filing)</li>
              </ul>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">2.2 Automatically Collected Information</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                When you visit our website, we automatically collect certain information about your device, including:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>IP address and browser type</li>
                <li>Operating system and device information</li>
                <li>Pages viewed and links clicked</li>
                <li>Time and date of visits</li>
                <li>Referring website addresses</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">3. How We Use Your Information</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We use the information we collect or receive to:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>Provide, maintain, and improve our trademark registration services</li>
                <li>Process your trademark applications and communicate with the USPTO</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send you administrative information, updates, and service-related communications</li>
                <li>Process payments and prevent fraudulent transactions</li>
                <li>Comply with legal obligations and regulatory requirements</li>
                <li>Analyze usage patterns to improve our website and services</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Protect against unauthorized access and security threats</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              
              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.1 With Government Agencies</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We share your information with the United States Patent and Trademark Office (USPTO) as necessary to process your trademark applications. Information submitted to the USPTO becomes part of the public record.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.2 With Service Providers</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We may share your information with third-party service providers who perform services on our behalf, including payment processing, data analysis, email delivery, hosting services, and customer service. These providers are contractually obligated to protect your information.
              </p>

              <h3 className="text-xl font-semibold text-[#3E2723] mb-3">4.3 Legal Requirements</h3>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We may disclose your information if required by law, court order, or governmental request, or when we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">5. Data Security</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li>SSL encryption for data transmission</li>
                <li>Secure servers and firewalls</li>
                <li>Regular security assessments and updates</li>
                <li>Restricted access to personal information on a need-to-know basis</li>
                <li>Employee training on data protection practices</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed">
                However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">6. Data Retention</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. For trademark-related information, we typically retain records for at least 7 years after the completion or termination of services, as required by professional standards and legal obligations.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">7. Your Privacy Rights</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-[#8D6E63] space-y-2 mb-4">
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Data portability:</strong> Request a copy of your information in a portable format</li>
                <li><strong>Objection:</strong> Object to certain processing activities</li>
              </ul>
              <p className="text-[#8D6E63] leading-relaxed">
                To exercise these rights, please contact us at {businessInfo.email} or {businessInfo.phone}.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are small data files stored on your device. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, some portions of our website may not function properly.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">9. Third-Party Links</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">10. Children's Privacy</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">11. California Privacy Rights</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete personal information, and the right to opt-out of the sale of personal information. We do not sell personal information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">12. International Data Transfers</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                Your information may be transferred to and maintained on servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using our services, you consent to the transfer of your information to the United States.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">13. Changes to This Privacy Policy</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-[#3E2723] mb-4">14. Contact Us</h2>
              <p className="text-[#8D6E63] leading-relaxed mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-[#EFEBE9] border-2 border-[#D7CCC8] rounded-lg p-6">
                <p className="text-[#3E2723] font-semibold mb-2">{businessInfo.name}</p>
                <p className="text-[#8D6E63]">{businessInfo.address}</p>
                <p className="text-[#8D6E63]">Email: {businessInfo.email}</p>
                <p className="text-[#8D6E63]">Phone: {businessInfo.phone}</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer onContactClick={handleContactClick} />
      <ContactPopup isOpen={isPopupOpen} onClose={handleClosePopup} />
      <Toaster />
    </div>
  );
};
